// utils/streamUtils.ts
import { API_CONFIG } from '~/config/api-endpoints.js'

export interface StreamConfig {
  id: string
  quality?: 'auto' | '480p' | '720p' | '1080p'
  format?: 'hls' | 'mp4' | 'dash'
}

export interface StreamSource {
  name: string
  url: string
  poster?: string
  quality?: string
}

export class ContentDeliveryClient {
  private headers: Record<string, string>

  constructor(headers: Record<string, string> = {}) {
    this.headers = {
      'Content-Type': 'application/json',
      ...headers
    }
  }

  // Generate stream URL with parameters (ใช้ API_CONFIG)
  getStreamUrl(config: StreamConfig): string {
    const params: Record<string, string> = {}
    
    if (config.quality && config.quality !== 'auto') {
      params.quality = config.quality
    }
    
    if (config.format) {
      params.format = config.format
    }

    return API_CONFIG.getStreamUrl(config.id, params)
  }

  // Get stream metadata (ใช้ API_CONFIG)
  async getStreamInfo(streamId: string) {
    try {
      const response = await fetch(API_CONFIG.getStreamInfoUrl(streamId), {
        headers: this.headers
      })
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error('Failed to get stream info:', error)
      throw error
    }
  }

  // Generate poster/thumbnail URL (DEPRECATED: ไม่มี thumbnail API, ใช้ autoCover แทน)
  getThumbnailUrl(streamId: string, time: number = 0): string {
    console.warn('⚠️ getThumbnailUrl is deprecated - use autoCover: true instead');
    return ''; // Return empty string instead of non-existent API endpoint
  }

  // Check if stream is available (ใช้ API_CONFIG)
  async checkStreamAvailability(streamId: string): Promise<boolean> {
    try {
      const response = await fetch(API_CONFIG.getStreamCheckUrl(streamId), {
        method: 'HEAD',
        headers: this.headers
      })
      return response.ok
    } catch (error) {
      console.error('Stream availability check failed:', error)
      return false
    }
  }

  // Get available quality options
  async getQualityOptions(streamId: string): Promise<string[]> {
    try {
      const info = await this.getStreamInfo(streamId)
      return info.qualities || ['auto']
    } catch (error) {
      console.warn('Failed to get quality options:', error)
      return ['auto']
    }
  }
}

// Default instance (ไม่ต้องส่ง baseUrl อีกแล้ว)
export const contentDeliveryClient = new ContentDeliveryClient()

// Helper functions
export const createStreamSource = (
  streamId: string, 
  name: string = 'Stream',
  quality: StreamConfig['quality'] = 'auto'
): StreamSource => {
  const config: StreamConfig = { id: streamId, quality }
  
  return {
    name: `${name} (${quality})`,
    url: contentDeliveryClient.getStreamUrl(config),
    poster: '', // ใช้ autoCover แทน thumbnail API ที่ไม่มีอยู่
    quality
  }
}

export const getMultiQualityStreams = (streamId: string, name: string = 'Stream'): StreamSource[] => {
  const qualities: StreamConfig['quality'][] = ['auto', '480p', '720p', '1080p']
  
  return qualities.map(quality => createStreamSource(streamId, name, quality))
}
